// Must be first: capture + strip the ?address= research pre-fill hint from the
// URL before AnalyticsProvider (below) initializes Mixpanel, so a participant's
// home address never reaches analytics / browser history.
import './utils/researchAddressBootstrap';
import './i18n';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { router } from './App';
import { NotificationsProvider } from '@toolpad/core';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import PageLoader from './ui-library/PageLoader';
import theme from './theme';
import { DirectionProvider } from './providers/DirectionProvider';
import { NotificationProvider } from './components/ui-library/NotificationProvider';
import { AnalyticsProvider } from './providers/AnalyticsProvider';
import { AnnouncementProvider } from './providers/AnnouncementProvider';
import { isEmbed } from './config/embed';
import { loadJamloopPixel } from './utils/jamloopPixel';
import { loadMetaPixel } from './utils/metaPixel';
import { loadGoogleAdsTag } from './utils/googleAdsTag';

// Before React mounts, so the landing redirect and re-renders cannot repeat them.
// All honor an opt-out of advertising partners and Global Privacy Control.
loadMetaPixel();
loadJamloopPixel();
loadGoogleAdsTag();

// Tell Chromium to overlay the on-screen keyboard instead of
// shrinking the layout viewport. Required so the layout + visual
// viewport sizes stay stable through keyboard transitions — the
// keyboard inset is then read via the VirtualKeyboard API in
// ``BallotSheetProvider`` and exposed as ``--keyboard-inset-px``,
// the single source consumers read.
//
// iOS Safari (no ``virtualKeyboard`` API) keeps its native
// ``position: fixed``-follows-visual-viewport behavior; the
// provider falls back to a visualViewport-based calc that produces
// the same ``--keyboard-inset-px`` semantics.
//
// Edge Android quirk: see ``BallotSheetProvider`` — the persistent
// bottom browser toolbar (~53 CSS px) makes the raw inset land too
// high relative to layout-viewport bottom; we subtract it at the
// source in the provider instead of having each consumer know.
if (
  typeof navigator !== 'undefined' &&
  'virtualKeyboard' in navigator &&
  // @ts-expect-error VirtualKeyboard API not in the default TS lib yet.
  navigator.virtualKeyboard
) {
  // @ts-expect-error see above.
  navigator.virtualKeyboard.overlaysContent = true;
}

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Suspense fallback={<PageLoader />}>
      <DirectionProvider>
        <AnnouncementProvider>
          <NotificationsProvider>
            <NotificationProvider>
              <AnalyticsProvider>
                <RouterProvider router={router} />
              </AnalyticsProvider>
            </NotificationProvider>
          </NotificationsProvider>
        </AnnouncementProvider>
      </DirectionProvider>
    </Suspense>
  </ThemeProvider>
);

// Notify the embedding widget loader that the app finished bootstrapping.
// The loader validates the message origin on its side; a wildcard target is
// safe here because the payload carries no sensitive data.
if (isEmbed() && window.parent !== window) {
  window.parent.postMessage({ type: 'ready' }, '*');
}
